import { VAPI_PUBLIC_KEY, VAPI_ASSISTANT_ID } from "./vapi.config";

export { VAPI_PUBLIC_KEY, VAPI_ASSISTANT_ID };

/**
 * Global singleton for the Vapi instance.
 */
let vapiInstance: any | null = null;
let isLoadingSdk = false;

const VAPI_CDN_URL = "https://cdn.jsdelivr.net/gh/VapiAI/html-script-tag@latest/dist/assets/index.js";

/**
 * Dynamic loader for the Vapi SDK from CDN.
 */
export async function loadVapiSdk() {
  if (typeof window === "undefined") return null;
  
  if (vapiInstance) return vapiInstance;
  
  if ((window as any).vapiSDK) {
    console.log("[vapi] vapiSDK found on window, initializing...");
    const sdk = (window as any).vapiSDK;
    vapiInstance = sdk.vapi || sdk;
    return vapiInstance;
  }

  if (isLoadingSdk) {
    for (let i = 0; i < 100; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      if (vapiInstance) return vapiInstance;
      if (!isLoadingSdk && (window as any).vapiSDK) {
        const sdk = (window as any).vapiSDK;
        vapiInstance = sdk.vapi || sdk;
        return vapiInstance;
      }
    }
  }

  isLoadingSdk = true;
  try {
    console.log("[vapi] Loading Vapi SDK from CDN...");
    
    let script = document.querySelector(`script[src="${VAPI_CDN_URL}"]`) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement("script");
      script.src = VAPI_CDN_URL;
      script.defer = true;
      // Prevent the SDK from auto-mounting its default widget
      script.setAttribute("data-vapi-support-btn", "false");
      document.head.appendChild(script);

    }

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Vapi SDK load timeout (15s)"));
      }, 15000);

      const check = () => {
        if ((window as any).vapiSDK) {
          clearTimeout(timeout);
          resolve(true);
        } else {
          setTimeout(check, 100);
        }
      };
      
      script.onload = () => check();
      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("Failed to load Vapi SDK script from CDN"));
      };
      check();
    });

    console.log("[vapi] SDK loaded successfully from CDN.");
    const sdk = (window as any).vapiSDK;
    vapiInstance = sdk.vapi || sdk;
    
    if (vapiInstance) {
      console.log("[vapi] Global instance verified.", {
        hasOn: typeof vapiInstance.on === 'function',
        hasStart: typeof vapiInstance.start === 'function'
      });
    }
    return vapiInstance;
  } catch (err: any) {
    console.error("[vapi] Error loading Vapi CDN:", err);
    throw err;
  } finally {
    isLoadingSdk = false;
  }
}

export const getVapi = () => vapiInstance;

export const VAPI_TOOLS = [
  {
    name: "get_meal_plans",
    description: "Get a list of available healthy meal plans and their benefits.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_todays_dishes",
    description: "AUTHORITATIVE LIVE MENU TOOL. You MUST call this tool whenever the customer asks about today's breakfast, lunch, snacks, dinner, today's dishes, today's menu, available dishes, or what food can be ordered today. NEVER answer these questions from model knowledge or general info.",
    parameters: {
      type: "object",
      properties: {
        day: { type: "string", enum: ["today", "tomorrow"] },
        meal_slot: { type: "string", enum: ["breakfast", "lunch", "snacks", "dinner"], description: "Filter by a specific meal slot." }
      }
    }
  },
  {
    name: "get_meal_plan_quote",
    description: "AUTHORITATIVE PRICING TOOL. Get the exact price for a specific meal plan. You MUST map customer preferences to these codes: preference_code: 'veg', 'non-veg', 'eggetarian', 'veg_eggan', 'veg_nonveg'. combination_code: 'lunch_only', 'bf-only', 'dinner_only', 'sn-only'. duration_days: the total number of meals/days requested (e.g. 3, 15, 30, 60, 90). NEVER guess prices.",
    parameters: {
      type: "object",
      properties: {
        plan_slug: { type: "string", description: "The slug of the meal plan (e.g., 'kids-friendly')." },
        duration_days: { type: "number", description: "Number of meals/days (e.g., 3, 15, 30, 60, 90)." },
        combination_code: { type: "string", description: "Meal selection code: 'lunch_only', 'bf-only', 'dinner_only', 'sn-only'." },
        preference_code: { type: "string", description: "Food preference code: 'veg', 'non-veg', 'eggetarian', 'veg_eggan', 'veg_nonveg'." },
        delivery_area_name: { type: "string", description: "Name of the delivery area/neighborhood." }
      },
      required: ["plan_slug", "duration_days", "combination_code", "preference_code"]
    }
  },
  {
    name: "get_dish_nutrition",
    description: "AUTHORITATIVE NUTRITION TOOL. Get exact calories, protein, carbs, and fat for a specific dish. Use this whenever the customer asks 'How many calories?' or 'What is the protein content?'.",
    parameters: {
      type: "object",
      properties: {
        dish_id: { type: "string", description: "The unique ID of the dish." }
      },
      required: ["dish_id"]
    }
  },
  {
    name: "get_delivery_cutoffs",
    description: "AUTHORITATIVE LIVE DELIVERY CUTOFF TOOL. You MUST call this tool whenever the customer asks about an ordering cutoff, ordering deadline, when a meal closes, or the latest time they can order. CLEARLY DISTINGUISHES between A La Carte and Meal Plan rules.",
    parameters: { 
      type: "object", 
      properties: {
        type: { type: "string", enum: ["alacarte", "meal_plan"], description: "The type of order to check." },
        meal_slot: { type: "string", enum: ["breakfast", "lunch", "snacks", "dinner"], description: "The specific meal slot to check (required for alacarte)." }
      },
      required: ["type"]
    }
  },
  {
    name: "get_delivery_areas",
    description: "Verify if Healthy Kitchen delivers to a specific neighborhood or area in Mumbai.",
    parameters: {
      type: "object",
      properties: {
        neighborhood: { type: "string", description: "The name of the area to check." }
      }
    }
  },
  {
    name: "get_auth_status",
    description: "Check if the customer is signed in. Required for account-specific info.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_my_subscription",
    description: "AUTHORITATIVE CUSTOMER TOOL. Use this whenever the customer asks about their own subscription, meal plan details, start/end dates, or status. NEVER answer from general knowledge.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_my_meals",
    description: "AUTHORITATIVE CUSTOMER TOOL. Use this whenever the customer asks about how many meals they have left, remaining meals, or used meals. NEVER answer from general knowledge.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_my_orders",
    description: "AUTHORITATIVE CUSTOMER TOOL. Use this whenever the customer asks about their recent orders, last order, or order status. NEVER answer from general knowledge.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_my_wallet",
    description: "AUTHORITATIVE CUSTOMER TOOL. Use this whenever the customer asks about their wallet balance or recent wallet transactions. NEVER answer from general knowledge.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_my_delivery_status",
    description: "AUTHORITATIVE CUSTOMER TOOL. Use this whenever the customer asks about their next delivery, upcoming delivery, or delivery timing. NEVER answer from general knowledge.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "get_my_delivery_address",
    description: "AUTHORITATIVE CUSTOMER TOOL. Use this whenever the customer asks about their delivery address or where their food is being delivered. NEVER answer from general knowledge.",
    parameters: { type: "object", properties: {} }
  },
  {
    name: "search_alacarte_items",
    description: "Search for available food items in the catalog. Use for questions like 'What can I order for lunch?' or 'Do you have paneer dishes?'.",
    parameters: {
      type: "object",
      properties: {
        q: { type: "string", description: "Search query." },
        meal_slot: { type: "string", enum: ["breakfast", "lunch", "snacks", "dinner"], description: "Filter by meal slot." },
        is_veg: { type: "boolean", description: "Filter for vegetarian items." }
      }
    }
  },
  {
    name: "get_alacarte_item",
    description: "Get detailed information about a specific item.",
    parameters: {
      type: "object",
      properties: { item_id: { type: "string" } },
      required: ["item_id"]
    }
  },
  {
    name: "add_to_cart",
    description: "MUTATION. Add an item to the cart. You MUST confirm item, price, and quantity with the customer first. NEVER execute silently.",
    parameters: {
      type: "object",
      properties: {
        dish_id: { type: "string" },
        slot: { type: "string", enum: ["breakfast", "lunch", "snacks", "dinner"] },
        quantity: { type: "number", minimum: 1 },
        client_request_id: { type: "string", description: "Unique UUID for idempotency." }
      },
      required: ["dish_id", "slot", "client_request_id"]
    }
  },
  {
    name: "get_my_cart",
    description: "AUTHORITATIVE CUSTOMER TOOL. Retrieve the current contents of the authenticated customer's cart.",
    parameters: {
      type: "object",
      properties: { token: { type: "string" } },
      required: ["token"]
    }
  },
  {
    name: "update_cart_item",
    description: "MUTATION. Update item quantity in cart. Requires explicit customer confirmation.",
    parameters: {
      type: "object",
      properties: {
        item_id: { type: "string" },
        quantity: { type: "number", minimum: 1 },
        client_request_id: { type: "string" }
      },
      required: ["item_id", "quantity", "client_request_id"]
    }
  },
  {
    name: "remove_from_cart",
    description: "MUTATION. Remove an item from the cart. Requires explicit customer confirmation.",
    parameters: {
      type: "object",
      properties: {
        item_id: { type: "string" },
        client_request_id: { type: "string" }
      },
      required: ["item_id", "client_request_id"]
    }
  },
  {
    name: "start_checkout",
    description: "Initiate the secure checkout flow.",
    parameters: {
      type: "object",
      properties: { token: { type: "string" } },
      required: ["token"]
    }
  },
  {
    name: "get_consultation_availability",
    description: "Check for available health consultation slots for a specific date.",
    parameters: {
      type: "object",
      properties: { date: { type: "string", description: "YYYY-MM-DD" } },
      required: ["date"]
    }
  },
  {
    name: "book_consultation",
    description: "MUTATION. Book a health consultation. Requires explicit customer confirmation.",
    parameters: {
      type: "object",
      properties: {
        consultant_id: { type: "string" },
        type_id: { type: "string" },
        start_time: { type: "string", description: "ISO 8601" },
        date: { type: "string" }
      },
      required: ["consultant_id", "type_id", "start_time", "date"]
    }
  },
  {
    name: "navigate_to",
    description: "Navigate the user to a specific page on the website.",
    parameters: {
      type: "object",
      properties: {
        path: { 
         type: "string", 
          description: "The path to navigate to.",
          enum: ["/", "/meal-plans", "/weekly-menu", "/appointments", "/careers", "/blog", "/about", "/checkout"]
        }
      },
      required: ["path"]
    }
  }
];

if (typeof window !== "undefined") {
  (window as any).__VAPI_TOOLS__ = VAPI_TOOLS;
}
